function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { arrayOf, bool, elementType, exact, func, number, object, oneOf, oneOfType, shape, string, element } from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import assetResolver, { assetKeys } from 'mosaic-assets-resolver';
import Slide from '@mui/material/Slide';
import Modal from '@mui/material/Modal';
import SovosAccountSwitcher from '../sovos-account-switcher';
import SovosDialog from '../sovos-dialog';
import SovosDialogActions from '../sovos-dialog-actions';
import SovosDialogContent from '../sovos-dialog-content';
import SovosDialogTitle from '../sovos-dialog-title';
import SovosButton from '../sovos-button';
import NavigationLinks from './components/navigation-links/NavigationLinks';
import NavigationLinksMinimal from './components/navigation-links/NavigationLinksMinimal';
import NavigationFooter from './components/navigation-footer/NavigationFooter';
import { LinksContainer, LogoAndProduct, LogoAndProductContainer, NavContent, NavHeader, ProductLogo, ProductName, Root } from './components/styles/Styles';
import { useResize } from '../hooks';
import { ResizeContext, spacingToNumber } from '../internals/utils';
import HEADER from './constants/header';
import { isLinkSelected } from '../internals/navigation/linkUtils';
import useProductSwitcherProps from '../internals/navigation/useProductSwitcher';
import showAccountSwitcher from '../internals/navigation/accountSwitcherUtils';
export const navigationClassName = 'sovosNavigation';

/**
 * In most applications this component will be rendered by
 * `SovosGlobalLayout`. It can be used directly for custom situations that
 * do not use the global layout. The design is responsive with automatic collapsing.
 *
 * Display a list of links in the navigation through the `link` property. May contain nested items and groups divided into sections. Relates to other properties such as: `initialSelectedLink`.
 *
 * Display multiples products to be selected in the product switcher through the `slotProps.productSwitchr.products` property, does not render when `length` equal to or less than `1`, or when `showProductSwitcher` is set to `false`.
 *
 * Display multiples accounts to be selected in the navigation through the `accounts` property, does not render when `length` equal to or less than `1`, if `showSingleAccount` is set `true` display account name when single account. Relates to other properties and methods such as: `accountsAreLoading`; `accountSwitcherLabels`; `didAccountChange`; `minimumAccountSearchCharacters`; `selectedAccount`; `showSingleAccount`; `setAccount`; `onAccountSearch`; `onExpandAccount`.
 */
const SovosNavigation = _ref => {
  var _SovosAccountSwitcher, _LinksContainer;
  let {
    accounts,
    accountsAreLoading = false,
    accountSwitcherLabels,
    className,
    'data-testid': dataTestId,
    didAccountChange,
    FooterProps,
    initialSelectedLink,
    labels,
    links,
    minimumAccountSearchCharacters,
    onAccountSearch,
    onExpandAccount,
    onSelectProduct,
    product,
    products,
    productSwitcherLabel,
    saveNavCollapsed = false,
    selectedAccount,
    selectedLink: selectedLinkProp,
    setAccount,
    showProductSwitcher = true,
    showSingleAccount = false,
    slotProps = {},
    slots = {}
  } = _ref;
  const {
    breakpoints,
    components,
    spacing,
    transitions
  } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(!!(saveNavCollapsed && sessionStorage.getItem('navIsCollapsed') === 'true'));
  const isBreakpointSmall = useMediaQuery(breakpoints.down('md'));
  const [isModal, setIsModal] = useState(false);
  const isMinimal = !isModal && (isBreakpointSmall || isCollapsed);
  const [linkState, setLinkState] = useState(initialSelectedLink);
  const selectedLink = selectedLinkProp || linkState;
  const [confirmNavigationLinks, setConfirmNavigationLinks] = useState(undefined);
  const contentRef = useRef(null);
  const [contentRect] = useResize(contentRef);
  const {
    productSwitcher: productSwitcherSlotProps,
    ...footerSlotProps
  } = slotProps;
  const {
    productSwitcher: ProductSwitcherSlot,
    ...footerSlots
  } = slots;
  useEffect(() => {
    if (!isBreakpointSmall) {
      setIsModal(false);
    }
  }, [isBreakpointSmall]);
  const handleModalClose = useCallback(() => {
    if (isModal) setIsModal(false);
  }, [isModal]);
  const handleCollapseClick = () => {
    if (isMinimal && isBreakpointSmall) {
      setIsModal(true);
      return;
    }
    if (isModal) {
      setIsModal(false);
      return;
    }

    // collapsed nav session storage
    if (saveNavCollapsed) {
      const navIsCollapsed = sessionStorage.getItem('navIsCollapsed');
      if (navIsCollapsed === 'false') {
        sessionStorage.setItem('navIsCollapsed', 'true');
      } else {
        sessionStorage.setItem('navIsCollapsed', 'false');
      }
    }
    setIsCollapsed(prevState => !prevState);
  };
  const selectLink = link => {
    const {
      action,
      id,
      label
    } = link;
    action();
    if (!selectedLinkProp) {
      setLinkState(id !== null && id !== void 0 ? id : label);
    }
  };
  const handleSelectLink = link => {
    handleModalClose();
    const currentLink = links.find(l => isLinkSelected(l, selectedLink) || l.nestedLinks && l.nestedLinks.find(nl => isLinkSelected(nl, selectedLink)));
    if (currentLink !== null && currentLink !== void 0 && currentLink.confirmNavigationDialogProps && currentLink !== null && currentLink !== void 0 && currentLink.confirmNavigationDialogProps.shouldShowDialog()) {
      setConfirmNavigationLinks([currentLink, link]);
    } else {
      selectLink(link);
    }
  };
  const handleSetAccount = newContext => {
    handleModalClose();
    setAccount(newContext);
  };
  const [productMenuProps, ProductMenu] = useProductSwitcherProps({
    onSelectProduct,
    product,
    products,
    productSwitcherLabel,
    ProductSwitcherSlot,
    productSwitcherSlotProps
  });
  const hasProductsLength = Array.isArray(productMenuProps.products) && !!productMenuProps.products.length;
  const shouldShowProductSwitcher = showProductSwitcher && (hasProductsLength || ProductSwitcherSlot);
  const renderConfirmNavigationDialog = () => {
    if (!confirmNavigationLinks) {
      return undefined;
    }
    const [currentLink, targetLink] = confirmNavigationLinks;
    const {
      confirmNavigationDialogProps: {
        cancelButtonText,
        continueButtonText,
        content,
        title
      }
    } = currentLink;
    return /*#__PURE__*/React.createElement(SovosDialog, {
      onClose: () => setConfirmNavigationLinks(undefined),
      open: true,
      width: "small"
    }, /*#__PURE__*/React.createElement(SovosDialogTitle, null, title), /*#__PURE__*/React.createElement(SovosDialogContent, null, typeof content === 'function' ? content() : content), /*#__PURE__*/React.createElement(SovosDialogActions, null, /*#__PURE__*/React.createElement(SovosButton, {
      onClick: () => setConfirmNavigationLinks(undefined),
      variant: "text"
    }, cancelButtonText), /*#__PURE__*/React.createElement(SovosButton, {
      color: "error",
      onClick: () => {
        setConfirmNavigationLinks(undefined);
        selectLink(targetLink);
      }
    }, continueButtonText)));
  };
  const Links = isMinimal && !isModal ? NavigationLinksMinimal : NavigationLinks;
  function getLogoSrc() {
    var _components$SovosNavi;
    // force the logo src to evaluate late so that we can ensure the asset
    // resolver has been configured
    const logoOverride = components === null || components === void 0 || (_components$SovosNavi = components.SovosNavigation) === null || _components$SovosNavi === void 0 ? void 0 : _components$SovosNavi.logoUrl;
    return logoOverride ? "".concat(logoOverride) : "".concat(assetResolver.branding(assetKeys.branding.logoLight));
  }
  const renderProductSwitcher = () => {
    const popoverProps = {
      anchorOrigin: {
        horizontal: spacingToNumber(spacing(7)),
        vertical: 'top'
      }
    };

    // If we want to show the product switcher, render the slot component we've been given or render the default switcher when we have products
    return /*#__PURE__*/React.createElement(ProductMenu, _extends({
      onClose: handleModalClose,
      PopoverProps: popoverProps
    }, productMenuProps));
  };
  const renderNavigation = () => {
    var _productMenuProps$sel, _components$SovosNavi2;
    const productName = (_productMenuProps$sel = productMenuProps.selectedProduct) === null || _productMenuProps$sel === void 0 ? void 0 : _productMenuProps$sel.name;
    const shouldShowProductName = !!productName;
    return /*#__PURE__*/React.createElement(Root, {
      "aria-label": "Main",
      className: clsx(className, navigationClassName, isMinimal && "".concat(navigationClassName, "--minimal")),
      "data-testid": dataTestId,
      shouldShowProductSwitcher: shouldShowProductSwitcher
    }, /*#__PURE__*/React.createElement(NavContent, {
      className: "sovosNavigation__content",
      ref: contentRef
    }, /*#__PURE__*/React.createElement(NavHeader, null, /*#__PURE__*/React.createElement(LogoAndProductContainer, {
      shouldShowProductName: shouldShowProductName,
      sx: {
        minHeight: !isMinimal || isMinimal && shouldShowProductSwitcher ? spacing(5.5) : null
      }
    }, !isMinimal ? /*#__PURE__*/React.createElement(LogoAndProduct, null, /*#__PURE__*/React.createElement(ProductLogo, {
      alt: (components === null || components === void 0 || (_components$SovosNavi2 = components.SovosNavigation) === null || _components$SovosNavi2 === void 0 ? void 0 : _components$SovosNavi2.logoAlt) || 'Sovos',
      className: "sovosNavigation__logo",
      productName: productName,
      src: getLogoSrc()
    }), shouldShowProductName && /*#__PURE__*/React.createElement(ProductName, {
      productName: productName
    }, productName)) : null, shouldShowProductSwitcher ? renderProductSwitcher() : null), showAccountSwitcher({
      accounts,
      onAccountSearch,
      selectedAccount,
      showSingleAccount
    }) && (_SovosAccountSwitcher || (_SovosAccountSwitcher = /*#__PURE__*/React.createElement(SovosAccountSwitcher, {
      accounts: accounts,
      didAccountChange: didAccountChange,
      isLoading: accountsAreLoading,
      isMinimal: isMinimal,
      labels: accountSwitcherLabels,
      minimumSearchCharacters: minimumAccountSearchCharacters,
      onExpandAccount: onExpandAccount,
      onSearch: onAccountSearch,
      selectedAccount: selectedAccount,
      setAccount: handleSetAccount
    })))), _LinksContainer || (_LinksContainer = /*#__PURE__*/React.createElement(LinksContainer, null, /*#__PURE__*/React.createElement(Links, {
      key: selectedLink,
      links: links,
      onLinkClick: handleSelectLink,
      selectedLink: selectedLink
    }))), /*#__PURE__*/React.createElement(NavigationFooter, _extends({
      handleModalClose: handleModalClose,
      isCollapsed: isMinimal,
      onCollapseClicked: handleCollapseClick,
      labels: labels,
      slotProps: footerSlotProps,
      slots: footerSlots
    }, FooterProps))));
  };
  return /*#__PURE__*/React.createElement(ResizeContext.Provider, {
    value: contentRect
  }, isModal ? /*#__PURE__*/React.createElement(Modal, {
    open: isModal,
    onClose: handleModalClose
  }, /*#__PURE__*/React.createElement(Slide, {
    direction: "right",
    in: isModal,
    timeout: transitions.duration.enteringScreen
  }, renderNavigation())) : renderNavigation(), renderConfirmNavigationDialog());
};
SovosNavigation.propTypes = {
  /**
   * Array of available context objects
   *
   * **Note**: does not render when length is 0 or 1, as there is nothing
   * to _switch_ to
   */
  accounts: arrayOf(shape({
    name: string.isRequired,
    nestedAccounts: arrayOf(object),
    taxYear: string,
    type: oneOf(['production', 'test', 'default'])
  })),
  /**
   * If `true`, `onAccountSearch` is loading results
   */
  accountsAreLoading: bool,
  /**
   * Labels for searching in account switcher
   */
  accountSwitcherLabels: exact({
    loading: string,
    noResults: string,
    searchTemplate: string
  }),
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * Custom context equality check
   */
  didAccountChange: func,
  /**
   * @deprecated Properties for the sovos navigation footer child
   *   component. Alternatively, use `slotProps` and provide custom
   *   components properties. If `slotProps` are specified, this property
   *   will be ignored
   */
  FooterProps: shape({
    labels: exact({
      collapseIcon: string,
      helpIcon: string,
      moreIcon: string,
      settingsIcon: string,
      userIcon: string
    }),
    onHelpClicked: func,
    showSettingsButton: bool,
    showUserButton: bool,
    showHelpButton: bool,
    settingsMenuItems: arrayOf(shape({
      label: string,
      action: func
    })),
    userName: string.isRequired,
    userMenuItems: arrayOf(shape({
      label: string,
      action: func
    })),
    additionalButtons: arrayOf(shape({
      disabled: bool,
      icon: element.isRequired,
      label: string.isRequired,
      action: func.isRequired
    }))
  }),
  /**
   * Link selected on load. When specified, the component will manage the
   * link selection state.
   */
  initialSelectedLink: string,
  /**
   * Tooltips for navigation icon buttons
   */
  labels: exact({
    collapseIcon: string,
    helpIcon: string,
    moreIcon: string,
    settingsIcon: string,
    userIcon: string
  }),
  /**
   * Array of objects, each with:
   *
   * - `confirmationDialogProps`: object with
   *
   *   - `cancelButtonText`: string,
   *   - `continueButtonText`: string,
   *   - `content`: string or func, content of the dialog can be static or
   *       dynamically generated by a function
   *   - `shouldShowDialog`: func, if true, a dialog will be shown allowing the
   *       user to cancel or continue the navigation action
   *   - `title`: string,
   * - `id`: string or number
   * - `label`: string, content of the link
   * - `icon`: element, icon to display
   * - `action`: function, callback fired when the link is clicked
   * - `nestedLinks`: array, nested links
   * - `type`: 'content' or 'header', 'content is the default.
   *
   * `label` is always required. `icon` and either `action` OR
   * `nestedLinks` are required when `type` is not `header`
   *
   * `confirmationDialogProps` controls conditional navigation. The prop
   * object is optional but all values in the object are required. If
   * `confirmationDialogProps` is given and the `shouldShowDialog` function
   * returns true, the user will be given the option to cancel the navigation action
   */
  links: arrayOf(shape({
    action: props => {
      const {
        action,
        nestedLinks,
        type
      } = props;
      if (type === HEADER) {
        return null;
      }
      const baseError = '`SovosNavigation` received an invalid link: links should have either nestedLinks or an action function, but received';
      if (nestedLinks !== null && nestedLinks !== void 0 && nestedLinks.length) {
        if (action != null) {
          return new Error("".concat(baseError, " both."));
        }
      } else if (typeof action !== 'function') {
        return new Error("".concat(baseError, " neither."));
      }
      return null;
    },
    confirmNavigationDialogProps: shape({
      cancelButtonText: string.isRequired,
      continueButtonText: string.isRequired,
      content: oneOfType([func, string]).isRequired,
      shouldShowDialog: func.isRequired,
      title: string.isRequired
    }),
    disabled: bool,
    icon: elementType,
    id: oneOfType([string, number]),
    label: string.isRequired,
    nestedLinks: arrayOf(shape({
      action: func.isRequired,
      confirmNavigationDialogProps: shape({
        cancelButtonText: string.isRequired,
        continueButtonText: string.isRequired,
        content: oneOfType([func, string]).isRequired,
        shouldShowDialog: func.isRequired,
        title: string.isRequired
      }),
      disabled: bool,
      id: oneOfType([string, number]),
      label: string.isRequired,
      toolTip: string
    })),
    toolTip: string,
    type: oneOf(['content', HEADER])
  })).isRequired,
  /**
   * Minimum number of characters needed to trigger an asynchronous account search
   */
  minimumAccountSearchCharacters: number,
  /**
   * Callback fired when text is entered into the account search box. Used
   * to asynchronously search accounts. If `onAccountSearch` is provided,
   * `selectedAccount` is required. `function(searchVal: string) => void`
   */
  onAccountSearch: func,
  /**
   * Callback fired when a context is expanded. Used to asynchronously load
   * nested accounts `function(context: object) => void`
   */
  onExpandAccount: func,
  /**
   * @deprecated Callback fired when product selected from product switcher. Use `slotProps`
   */
  onSelectProduct: func,
  /**
   * @deprecated Product selected on load. Use `slotProps`
   */
  product: oneOfType([string, object]),
  /**
   * @deprecated
   * Array of objects, each with
   *
   * - `disabled` bool
   * - `icon` elementType
   * - `id` string or number. Required if there is any chance the names may
   *   not be unique. Not required for dividers and headers.
   * - `name` string
   * Use `slotProps`
   */
  products: arrayOf(shape({
    disabled: bool,
    icon: elementType,
    id: oneOfType([number, string]),
    logoMonoUrl: string,
    logoUrl: string,
    name: string
  })),
  /**
   * @deprecated
   * Title attribute applied to the product switcher component.
   * Use `slotProps`
   */
  productSwitcherLabel: string,
  /**
   * Store navigation collapsed state in session storage
   */
  saveNavCollapsed: bool,
  /**
   * When missing, invalid, or not in the accounts array, the first context
   * will be selected.
   */
  selectedAccount: shape({
    name: string.isRequired,
    taxYear: string,
    type: oneOf(['production', 'test', 'default'])
  }),
  /**
   * Link defined by the application. When specified, the application must
   * manage the link selection state.
   */
  selectedLink: props => {
    const {
      initialSelectedLink,
      selectedLink
    } = props;
    if (selectedLink && typeof selectedLink !== 'string') {
      return new Error('selectedLink must be a string');
    }
    if (initialSelectedLink && selectedLink) {
      return new Error('initialSelectedLink and selectedLink cannot both be defined');
    }
    return null;
  },
  /**
   * Callback fired when a new context is selected
   */
  setAccount: func,
  /**
   * Display product switcher
   */
  showProductSwitcher: bool,
  /**
   * Display account name when there is only one account
   */
  showSingleAccount: bool,
  /**
   * Properties for overrideable components
   */
  slotProps: shape({
    additionFooterIconButtons: arrayOf(shape({
      children: element.isRequired,
      disabled: bool,
      onClick: func.isRequired,
      tooltipText: string.isRequired
    })),
    helpButton: exact({
      onClick: func
    }),
    productSwitcher: shape({
      onSelectProduct: func,
      products: arrayOf(shape({
        disabled: bool,
        icon: elementType,
        id: oneOfType([number, string]),
        logoMonoUrl: string,
        logoUrl: string,
        name: string
      })),
      selectedProduct: object,
      supportItems: arrayOf(shape({
        description: string,
        disabled: bool,
        icon: elementType,
        linkText: string.isRequired,
        target: string,
        url: string.isRequired
      })),
      tooltipText: string
    }),
    settingsMenu: oneOfType([object, exact({
      items: arrayOf(shape({
        label: string,
        action: func
      }))
    })]),
    userMenu: oneOfType([object, exact({
      email: string,
      items: arrayOf(shape({
        action: func,
        icon: elementType,
        label: string,
        type: string
      })),
      name: string,
      surname: string
    })])
  }),
  /**
   * Overrideable components
   */
  slots: shape({
    productSwitcher: elementType,
    settingsMenu: elementType,
    userMenu: elementType
  })
};
export default SovosNavigation;